import { Controller, useFormContext } from 'react-hook-form';
import { useFirmContext } from '../../../providers/FirmProvider';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { SubscriptionTier } from '../../../types/firm';
import { WorkpaperAssignee, WorkpaperRoleEnum } from '../../../types/workpaper';
import { useIsFirmRole } from '../../../hooks/use-is-firm-role';
import { useIsWorkpaperRole } from '../../../hooks/use-is-workpaper-role';
import WorkpaperRoleSelect from '../../../components/user-select/WorkpaperRoleSelect';
import UpgradeHint from '../UpgradeHint';
import { useHasFeatureAccess } from '../../../hooks/use-has-feature-access';

type AssigneesSectionProps = {
    isLoading: boolean;
    onExternalUserError: (hasError: boolean) => void;
};

function AssigneesSection({ isLoading, onExternalUserError }: AssigneesSectionProps): JSX.Element {
    const { control, formState, trigger, watch } = useFormContext();
    const { readOnly } = useWorkpaperContext();
    const { isAdmin, isManager, isExternalUser } = useIsFirmRole();
    const { isApprover } = useIsWorkpaperRole();
    const { subscriptionVersion } = useFirmContext();

    const featureAccess = {
        canAssignMultipleApprovers: useHasFeatureAccess('workpaper-assign-multiple-approvers:write'),
        canAssignMultipleReviewers: useHasFeatureAccess('workpaper-assign-multiple-reviewers:write'),
        canAssignMultiplePreparers: useHasFeatureAccess('workpaper-assign-multiple-preparers:write'),
        canAssignGuests: useHasFeatureAccess('workpaper-assign-guests:write'),
    };

    const isSelectDisabled = isLoading || readOnly || isExternalUser;
    const canAssignApprover = isAdmin || isApprover;
    const canAssignAndInvite = isAdmin || isManager || isApprover;

    const approvers = watch('approvers') as WorkpaperAssignee[];
    const reviewers = watch('reviewers') as WorkpaperAssignee[];
    const preparers = watch('preparers') as WorkpaperAssignee[];
    const guests = watch('guests') as WorkpaperAssignee[];

    return (
        <div className="flex flex-col gap-2">
            <h3 className="font-medium text-gray-800 text-lg">Assigned</h3>
            <div className="flex flex-col gap-8">
                {/* APPROVERS */}
                <Controller
                    name="approvers"
                    rules={{ required: 'An approver is required.' }}
                    control={control}
                    render={({ field: { ref, onChange, value } }): JSX.Element => (
                        <div>
                            <WorkpaperRoleSelect
                                isMulti={featureAccess.canAssignMultipleApprovers}
                                isDisabled={isSelectDisabled || !canAssignApprover}
                                disabledUsers={[...reviewers, ...preparers, ...guests]}
                                errorMessage={formState.errors.approvers?.message?.toString()}
                                defaultValue={value}
                                innerRef={ref}
                                title="Approvers"
                                placeholder="Select approver(s)..."
                                canInvite={canAssignAndInvite}
                                role={WorkpaperRoleEnum.APPROVER}
                                disabledHint={!canAssignApprover ? 'This requires an approver or admin role.' : undefined}
                                onChange={(approvers): void => {
                                    onChange(approvers);
                                    trigger('approvers');
                                }}
                                onExternalUserError={onExternalUserError}
                            />
                            {subscriptionVersion === 1 && (
                                <UpgradeHint plan={SubscriptionTier.PROFESSIONAL} message="assign multiple approvers" />
                            )}
                        </div>
                    )}
                />
                {/* REVIEWERS */}
                <Controller
                    name="reviewers"
                    control={control}
                    render={({ field: { ref, onChange, value } }): JSX.Element => (
                        <div>
                            <WorkpaperRoleSelect
                                isOptional
                                isMulti={featureAccess.canAssignMultipleReviewers}
                                isDisabled={isSelectDisabled}
                                disabledUsers={[...approvers, ...preparers, ...guests]}
                                errorMessage={formState.errors.reviewers?.message?.toString()}
                                defaultValue={value}
                                innerRef={ref}
                                title="Reviewers"
                                placeholder="Select reviewer(s)..."
                                canInvite={canAssignAndInvite}
                                role={WorkpaperRoleEnum.REVIEWER}
                                onChange={(reviewers): void => {
                                    onChange(reviewers);
                                    trigger('reviewers');
                                }}
                                onExternalUserError={onExternalUserError}
                            />
                            {subscriptionVersion === 1 && (
                                <UpgradeHint plan={SubscriptionTier.ESSENTIAL} message="assign multiple reviewers" />
                            )}
                        </div>
                    )}
                />
                {/* PREPARERS */}
                <Controller
                    name="preparers"
                    control={control}
                    render={({ field: { ref, onChange, value } }): JSX.Element => (
                        <div>
                            <WorkpaperRoleSelect
                                isOptional
                                isMulti={featureAccess.canAssignMultiplePreparers}
                                isDisabled={isSelectDisabled}
                                disabledUsers={[...approvers, ...reviewers, ...guests]}
                                errorMessage={formState.errors.preparers?.message?.toString()}
                                defaultValue={value}
                                innerRef={ref}
                                title="Preparers"
                                placeholder="Select preparer(s)..."
                                canInvite={canAssignAndInvite}
                                role={WorkpaperRoleEnum.PREPARER}
                                onChange={(preparers): void => {
                                    onChange(preparers);
                                    trigger('preparers');
                                }}
                                onExternalUserError={onExternalUserError}
                            />
                            {subscriptionVersion === 1 && (
                                <UpgradeHint plan={SubscriptionTier.ESSENTIAL} message="assign multiple preparers" />
                            )}
                        </div>
                    )}
                />
                {/* GUESTS */}
                <Controller
                    name="guests"
                    control={control}
                    render={({ field: { ref, onChange, value } }): JSX.Element => (
                        <div>
                            <WorkpaperRoleSelect
                                isMulti
                                isOptional
                                isDisabled={isLoading || !featureAccess.canAssignGuests || !canAssignAndInvite || isExternalUser}
                                innerRef={ref}
                                canInvite={canAssignAndInvite}
                                role={WorkpaperRoleEnum.GUEST}
                                disabledUsers={[...approvers, ...reviewers, ...preparers]}
                                errorMessage={formState.errors.guests?.message?.toString()}
                                defaultValue={value}
                                title="Guests"
                                placeholder="Select guest(s)..."
                                disabledHint={!canAssignAndInvite ? 'This requires an approver or manager or admin role.' : undefined}
                                onChange={(guests): void => {
                                    onChange(guests);
                                    trigger('guests');
                                }}
                                onExternalUserError={onExternalUserError}
                            />
                            {subscriptionVersion === 1 && <UpgradeHint plan={SubscriptionTier.PROFESSIONAL} message="invite guests" />}
                        </div>
                    )}
                />
            </div>
        </div>
    );
}

export default AssigneesSection;
